// react
import { Suspense, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useParams } from '@tanstack/react-router';

// material ui icons
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import PercentRoundedIcon from '@mui/icons-material/PercentRounded';

// material ui
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Fade,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

// api
import {
  useGetDurationAnalysisQuery,
  useGetSeuDurationAnalysisQuery,
} from '@/api/energy-analysis/calculateEnergySavingsApiSlice';
import { useGetEnergyGroupQuery } from '@/api/generated/recalc/endpoints/v2/energyGroups';

// shared components
import PageContainer from '@/components/layout/PageContainer';
import InfoTooltipIcon from '@/components/ui/icon/InfoTooltipIcon';

// feature-components
import DurationAnalysisChart from '@/features/energyxplorer/components/analysis/DurationAnalysisChart';
import EnergyAnalyzerFilterInput from '@/features/energyxplorer/components/analysis/EnergyAnalyzerFilterInput';
import EnergyTreeNodeTraversal from '@/features/energyxplorer/components/nodes/EnergyTreeNodeTraversal';

// feature-hooks
// * Hooks
import { useEnergyTreeNode } from '@/features/energyxplorer/hooks/useEnergyTreeNode';
import useUrlAnalysisScope from '@/features/energyxplorer/hooks/useUrlAnalysisScope';
import useUrlFilters from '@/features/energyxplorer/hooks/useUrlFilters';
import useUrlSeu from '@/features/energyxplorer/hooks/useUrlSeu';

// components
import { useUrlDateRange } from '@/hooks/useUrlDateRange';

// relative
import EnergyTreeNodeAnalysisToolbar from './EnergyTreeNodeAnalysisToolbar';

import { DateTime } from 'luxon';

function shouldSkipQuery({ filters, selectedEnergyGroup, energyTreeNode }) {
  if (!energyTreeNode) return true;

  const hasActivePeriodFilter =
    filters &&
    Object.prototype.hasOwnProperty.call(filters, 'activePeriodFilter');

  const isPortfolio = selectedEnergyGroup?.type?.includes('Portfolio') === true;

  const hasOpeningHours = !!energyTreeNode?.options?.openingHours;

  // We SKIP when portfolio nodes lack opening hours AND no activePeriodFilter
  return isPortfolio && !hasOpeningHours && hasActivePeriodFilter;
}

// TODO: Move this to utils file at some point
const formatDate = (date) => {
  const day = date.setLocale('da').toFormat('d.');
  const month = date.setLocale('da').toFormat('MMMM');
  return `${day} ${month}`;
};

const EnergyTreeNodeDurationAnalysisPage = () => {
  const { t } = useTranslation('energyxplorer-analysis-tabs');

  const { accountId, energyGroupId } = useParams({ strict: false });
  const { timeRange } = useUrlDateRange();
  const { data: selectedEnergyGroup } = useGetEnergyGroupQuery(
    { groupId: energyGroupId },
    { skip: !energyGroupId },
  );
  // filters in URL
  const { filters: durationFilters } = useUrlFilters({}); // default filters here if you have them
  // analysis mode in URL
  const [analysisScope] = useUrlAnalysisScope({
    allowed: ['node', 'seu'],
    fallback: 'node',
  });
  const { energyTreeNode, nodeId, setNodeId } = useEnergyTreeNode({
    extraSkip: !selectedEnergyGroup, // don’t fetch if group not loaded
  });

  const shouldSkipDataFetch = shouldSkipQuery({
    filters: durationFilters,
    selectedEnergyGroup,
    energyTreeNode,
  });

  const nodeGroupsByType = useMemo(() => {
    const map = new Map();
    selectedEnergyGroup?.energyTreeNodes?.forEach((node) => {
      const type = node?.type;
      if (type) {
        if (!map.has(type)) map.set(type, []);
        map.get(type).push(node);
      }
    });
    return map;
  }, [selectedEnergyGroup]);

  const validSeuOptions = useMemo(() => {
    return Array.from(nodeGroupsByType.entries())
      .filter(([, nodes]) =>
        nodes.some((node) => node?.dataStreams?.length > 0),
      )
      .map(([type]) => type)
      .filter(
        (type) =>
          type !== 'Hovedmåler' && type !== 'Tavle' && type !== 'Undertavle',
      );
  }, [nodeGroupsByType]);

  const [seuToAnalyse] = useUrlSeu({
    key: 'type',
    allowed: validSeuOptions,
    fallback: () => validSeuOptions[0] ?? null,
  });

  const selectedRange = useMemo(
    () => [
      DateTime.fromMillis(timeRange.start).setZone('Europe/Copenhagen'),
      DateTime.fromMillis(timeRange.end).setZone('Europe/Copenhagen'),
    ],
    [timeRange],
  );

  const {
    data: durationAnalysisData,
    isLoading: durationAnalysisDataLoading,
    isFetching: durationAnalysisDataFetching,
    isUninitialized: isDurationAnalysisDataUninitialized,
    error: durationAnalysisDataError,
  } = useGetDurationAnalysisQuery(
    {
      accountId: accountId,
      energyGroupId: energyGroupId,
      nodeId: nodeId,
      start: selectedRange[0].toMillis(),
      end: selectedRange[1].toMillis(),
      ...durationFilters,
    },
    {
      skip: analysisScope !== 'node' || shouldSkipDataFetch,
    },
  );

  const {
    data: seuDurationAnalysisData,
    isLoading: seuDurationAnalysisDataLoading,
    isFetching: seuDurationAnalysisDataFetching,
    isUninitialized: isSeuDurationAnalysisDataUninitialized,
    error: seuDurationAnalysisDataError,
  } = useGetSeuDurationAnalysisQuery(
    {
      accountId: accountId,

      energyGroupId: energyGroupId,
      seu: seuToAnalyse,
      start: selectedRange[0].toMillis(),
      end: selectedRange[1].toMillis(),
      ...durationFilters,
    },
    {
      skip: analysisScope !== 'seu' || shouldSkipDataFetch,
    },
  );

  const dispatch = useDispatch();

  const [filtersOpen, setFiltersOpen] = useState(true);
  const [xAxisMode, setXAxisMode] = useState('percentage');
  const [data, setData] = useState(null);

  const isQueryUninitialized =
    (analysisScope === 'node' && isDurationAnalysisDataUninitialized) ||
    (analysisScope === 'seu' && isSeuDurationAnalysisDataUninitialized);

  const showLoading =
    durationAnalysisDataLoading ||
    durationAnalysisDataFetching ||
    seuDurationAnalysisDataLoading ||
    seuDurationAnalysisDataFetching ||
    isQueryUninitialized;

  useEffect(() => {
    const updateDataAsync = async (nextData) => {
      setData(nextData ?? null);
    };

    if (shouldSkipDataFetch) {
      updateDataAsync(null);
      return;
    }

    // Helper: clear data on error (snackbar handled by apiErrorMiddleware)
    const handleError = () => {
      updateDataAsync(null);
    };

    if (analysisScope === 'node') {
      if (durationAnalysisDataError) {
        handleError(durationAnalysisDataError);
        return;
      }
      updateDataAsync(durationAnalysisData);
    }

    if (analysisScope === 'seu') {
      if (seuDurationAnalysisDataError) {
        handleError(seuDurationAnalysisDataError);
        return;
      }
      updateDataAsync(seuDurationAnalysisData);
    }
  }, [
    analysisScope,
    dispatch,
    durationAnalysisData,
    durationAnalysisDataError,
    seuDurationAnalysisData,
    seuDurationAnalysisDataError,
    seuToAnalyse,
    shouldSkipDataFetch,
  ]);

  useEffect(() => {
    if (!nodeId && selectedEnergyGroup?.energyTreeNodes.length > 0) {
      setNodeId(selectedEnergyGroup?.energyTreeNodes[0]?.energyTreeNodeId);
    }
  }, [selectedEnergyGroup, nodeId, setNodeId]);

  const readyToRender =
    durationFilters && selectedEnergyGroup && energyTreeNode;

  return (
    <PageContainer sx={{ pt: 0 }}>
      {readyToRender && (
        <Fade in={readyToRender} timeout={500}>
          <Grid container spacing={2}>
            <Grid
              sx={{ transition: 'flex-basis 0.3s ease' }}
              size={{
                md: 12,
                lg: filtersOpen ? 9 : 12
              }}>
              <Card variant="outlined">
                <CardHeader
                  title={
                    <Box display="flex" alignItems="center" gap={1}>
                      {analysisScope === 'node'
                        ? `${energyTreeNode?.name} (Node)`
                        : `${seuToAnalyse} (SEU)`}
                      <InfoTooltipIcon
                        title={t(
                          'duration_analysis.tooltips.duration_analysis_description',
                        )}
                      />
                    </Box>
                  }
                  subheader={
                    selectedRange[0] && selectedRange[1]
                      ? `${t('duration_analysis.in_period')} ${formatDate(selectedRange[0])} - ${formatDate(selectedRange[1])}`
                      : t('duration_analysis.select_period')
                  }
                  action={
                    <EnergyTreeNodeAnalysisToolbar
                      filtersOpen={filtersOpen}
                      onToggleFilters={() => setFiltersOpen((v) => !v)}
                      showFilterToggle
                      filterToggleTooltip={{
                        open: t('duration_analysis.hide_filters'),
                        closed: t('duration_analysis.show_filters'),
                      }}
                      actions={
                        <ToggleButtonGroup
                          className="non-printable"
                          value={xAxisMode}
                          size="small"
                          exclusive
                          onChange={(e, newValue) => {
                            if (newValue) setXAxisMode(newValue);
                          }}
                        >
                          <ToggleButton
                            sx={{ borderRadius: 2 }}
                            value="duration"
                          >
                            <AccessTimeRoundedIcon
                              fontSize="small"
                              sx={{ mr: 1 }}
                            />
                            {t(
                              'duration_analysis.tooltips.shows_axis_in_hours',
                            )}
                          </ToggleButton>
                          <ToggleButton
                            sx={{ borderRadius: 2 }}
                            value="percentage"
                          >
                            <PercentRoundedIcon
                              fontSize="small"
                              sx={{ mr: 1 }}
                            />
                            {t(
                              'duration_analysis.tooltips.shows_axis_in_percent',
                            )}
                          </ToggleButton>
                        </ToggleButtonGroup>
                      }
                    />
                  }
                />
                <CardContent>
                  <DurationAnalysisChart
                    data={data}
                    xAxisMode={xAxisMode}
                    isLoading={showLoading}
                    querySkipped={shouldSkipDataFetch}
                  />
                </CardContent>
              </Card>
            </Grid>

            {filtersOpen && (
              <Grid
                className="non-printable"
                size={{
                  md: 12,
                  lg: 3
                }}>
                <Grid size="grow">
                  <Card sx={{ mb: 2 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography
                          variant="body1"
                          color="textSecondary"
                          fontWeight="bold"
                        >
                          {t('duration_analysis.filtering')}
                        </Typography>
                        <Tooltip
                          title={t('duration_analysis.filtering_description')}
                          placement="right"
                        >
                          <InfoRoundedIcon
                            fontSize="small"
                            sx={{
                              color: (theme) =>
                                theme.palette.mode === 'light'
                                  ? 'lightgray'
                                  : 'gray',
                              ml: 1,
                            }}
                          />
                        </Tooltip>
                      </Box>
                      <Suspense
                        fallback={
                          <div>
                            {t('analysis.loading', { ns: 'energyxplorer' })}
                          </div>
                        }
                      >
                        <EnergyAnalyzerFilterInput
                          selectedEnergyGroup={selectedEnergyGroup}
                          scope="duration"
                        />
                      </Suspense>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid size="grow">
                  <EnergyTreeNodeTraversal showModeToggle={true} />
                </Grid>
              </Grid>
            )}
          </Grid>
        </Fade>
      )}
    </PageContainer>
  );
};

export default EnergyTreeNodeDurationAnalysisPage;
